import { BroadcastChannel } from 'broadcast-channel';
import type { Handler } from 'mitt';
import { SIRONA_BROADCAST_CHANNEL, type Payload, type Source } from '../constants/crossTabEvents';
import { NameSpaces } from '../constants/namespaces';
import { emitter } from './emitter';

type CrossTabMessage<R> = {
  namespace: NameSpaces;
  payload: Payload<R>;
};

export type Unsubscribe = () => void;

/**
 * Core cross-tab subscription logic shared between subscribeCrossTabEvent and useCrossTabEventsListener.
 * Subscribes to events from both remote tabs (via BroadcastChannel) and local tab (via emitter).
 *
 * @param namespace - The namespace to subscribe to
 * @param onEvent - Callback invoked when events are received
 * @param broadcastChannel - The BroadcastChannel instance to use
 * @returns Cleanup function to unsubscribe from all events
 */
export function createCrossTabSubscription<R>(
  namespace: NameSpaces,
  onEvent: (args: { source: Source; payload: Payload<R> }) => void,
  broadcastChannel: BroadcastChannel<CrossTabMessage<R>>
): Unsubscribe {
  const bcHandler = ({ namespace: receivedNamespace, payload }: CrossTabMessage<R>) => {
    if (receivedNamespace !== namespace) {
      return;
    }

    onEvent({ source: 'remote', payload });
  };

  const localHandler: Handler<unknown> = (payload?: unknown) => {
    if (payload == null) {
      return;
    }

    onEvent({ source: 'local', payload: payload as Payload<R> });
  };

  broadcastChannel.addEventListener('message', bcHandler);
  emitter.on(namespace, localHandler);

  return () => {
    broadcastChannel.removeEventListener('message', bcHandler);
    emitter.off(namespace, localHandler);
  };
}

/**
 * Shared singleton BroadcastChannel instance for subscribeCrossTabEvent.
 * Lazily initialized on first use.
 */
let sharedChannel: BroadcastChannel<CrossTabMessage<unknown>> | null = null;

/**
 * Gets or creates the shared singleton BroadcastChannel instance.
 * Used by subscribeCrossTabEvent to maintain a single channel across all subscriptions.
 */
export const getSharedChannel = (): BroadcastChannel<CrossTabMessage<unknown>> => {
  if (sharedChannel == null) {
    sharedChannel = new BroadcastChannel<CrossTabMessage<unknown>>(SIRONA_BROADCAST_CHANNEL);
  }

  return sharedChannel;
};
